import _ from 'underscore';
import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Dropdown, TextField } from '@fluentui/react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ConversionHelpContext } from '@bingads-webui-campaign-react/conversion-help';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';

import { getStyles } from './get-styles';

export const CustomEventAction = (props) => {
  const {
    goals,
    getValueErrorMessage,
    actionOperator,
    onActionOperatorChange,
    action,
    onActionChange,
    onNotifyValidationResult,
  } = props;

  const i18n = useContext(I18nContext);
  const helpIds = useContext(ConversionHelpContext);
  const classes = useDefaultStyles(getStyles);
  const duplicateUrlErrorText = useMemo(() => i18n.getString(_TL_('Goal with this action and operator already exists')), [i18n]);

  const checkDuplicateAction = useCallback((actionValue, operator) => {
    const getGoalByUrlAndActionOperator = _.find(goals, g => g.action === actionValue && g.actionOperator === operator);

    if (getGoalByUrlAndActionOperator) {
      return duplicateUrlErrorText;
    }
    return undefined;
  }, [duplicateUrlErrorText, goals]);

  const actionErrorMessage = useMemo(() => {
    let errorMessage = '';

    // MT will trim the action field before writing to DB
    const trimmedAction = action && action.trim();

    errorMessage = getValueErrorMessage(i18n, 75, trimmedAction);
    if (_.isEmpty(errorMessage)) {
      // check for goals with the same action and action operator
      errorMessage = checkDuplicateAction(trimmedAction, actionOperator);
    }

    // defer onNotifyValidationResult call so parent state doesn't get modified during initial render
    _.defer(onNotifyValidationResult, errorMessage);

    return errorMessage;
  }, [action, actionOperator, checkDuplicateAction, getValueErrorMessage, i18n, onNotifyValidationResult]);

  const onOperatorChange = useCallback((_event, option) => {
    onActionOperatorChange(option);
  }, [onActionOperatorChange]);

  const onTextChange = useCallback((value) => {
    onActionChange(value);
  }, [onActionChange]);

  return (
    <div className="goal-action-form">
      <p className={classNames('action-title', classes.actionTitle)}>{i18n.getString(_TL_('Custom event parameters'))}</p>
      <p>{i18n.getString(_TL_('Select the parameters you\'d like to send with your event.'))}
        <LearnMoreLink topic={helpIds.customEventParametersLearnMore} linkText={i18n.getString(_TL_('Learn more'))} />
      </p>


      <div className={classNames('action-container', classes.actionContainer)}>
        <p className={classes.actionText}>{i18n.getString(_TL_('Action'))}</p>
        <Dropdown
          className={classNames('action-operator', classes.actionOperator)}
          selectedKey={actionOperator}
          options={[{
            key: 'EqualsTo',
            text: i18n.getString(_TL_('Equals to')),
            title: i18n.getString(_TL_('Equals to')),
          }]}
          onChange={onOperatorChange}
          ariaLabel={i18n.getString(_TL_('Select action operator'))}
        />
        <TextField
          className="action-input"
          ariaLabel={i18n.getString(_TL_('Event action'))}
          value={action}
          onChange={({ target }) => onTextChange((target || {}).value)}
          errorMessage={actionErrorMessage}
        />
      </div>
    </div>);
};

CustomEventAction.propTypes = {
  goals: PropTypes.arrayOf(PropTypes.shape({
    action: PropTypes.string,
    actionOperator: PropTypes.string,
  })),
  getValueErrorMessage: PropTypes.func.isRequired,
  action: PropTypes.string,
  onActionChange: PropTypes.func.isRequired,
  onNotifyValidationResult: PropTypes.func,
  actionOperator: PropTypes.oneOf(['EqualsTo']).isRequired,
  onActionOperatorChange: PropTypes.func.isRequired,
};

CustomEventAction.defaultProps = {
  goals: undefined,
  onNotifyValidationResult: null,
  action: '',
};
